import PropTypes from "prop-types";
import MathEditor from "./MathEditor";

/**
 * Adapter that exposes the same prop API as the legacy `DescriptionEditor`
 * (Jodit) but renders the CKEditor-based `MathEditor` underneath.
 *
 * Use this anywhere you'd currently use `DescriptionEditor` and want the
 * richer paste-from-Word / images / math-formula behaviour. The signatures
 * of `setState` are preserved verbatim:
 *
 *   - `setState(count, head, target, value)`  — redux dispatch style
 *   - `setState(count, target, value)`        — array-of-objects state
 *   - `setState(count, value)`                — array state
 *   - `setState(target, value)`               — object state
 *   - `setState(value)`                       — plain state
 *
 * The selection is made the same way `DescriptionEditor` does, based on
 * which of `count` / `head` / `target` were provided by the caller.
 */
export default function MathDescriptionEditor({
  state,
  setState,
  count,
  head,
  target,
  placeholder,
  readOnly,
  disabled,
  grayedOut,
  height,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
}) {
  const onChangeState = (value) => {
    if (count !== undefined && head !== undefined && target !== undefined) {
      return setState(count, head, target, value);
    }
    if (count !== undefined && target !== undefined) {
      return setState(count, target, value);
    }
    if (count !== undefined) return setState(count, value);
    if (target !== undefined) return setState(target, value);
    return setState(value);
  };

  const wrapperStyle = {
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    width: "100%",
  };

  // `disabled` / `readOnly` / `grayedOut` all collapse to a single
  // non-editable state in CKEditor.
  const nonEditable = Boolean(readOnly || disabled || grayedOut);

  // CKEditor sizes itself by content; we just enforce a minimum height
  // that mirrors what `DescriptionEditor` did with Jodit's `height` config.
  const editorStyle = height ? { minHeight: height } : undefined;

  return (
    <div style={wrapperStyle}>
      <MathEditor
        value={state}
        onChange={onChangeState}
        readOnly={nonEditable}
        placeholder={placeholder}
        style={editorStyle}
      />
    </div>
  );
}

MathDescriptionEditor.propTypes = {
  state: PropTypes.string,
  setState: PropTypes.func.isRequired,
  count: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  head: PropTypes.string,
  target: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  grayedOut: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  marginLeft: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  marginRight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  marginTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  marginBottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
